/**
 * Print Helper Utility
 * Provides common print toolbar and styles for all print windows
 */

/**
 * Escapes HTML special characters to prevent XSS
 * Use this for user-provided content that needs to be displayed in print
 * @param {string} str - String to escape
 * @returns {string} - Escaped string safe for HTML
 */
export const escapeHtml = (str) => {
  if (typeof str !== 'string') return String(str || '');
  const htmlEscapes = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  };
  return str.replace(/[&<>"']/g, (char) => htmlEscapes[char]);
};

/**
 * Opens a print window safely
 * @param {string} html - The complete HTML document to print
 * @returns {Window|null} - The opened window or null if blocked
 */
export const openPrintWindow = (html) => {
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  if (!printWindow) {
    console.error('Failed to open print window - popup may be blocked');
    return null;
  }
  printWindow.document.write(html);
  printWindow.document.close();
  return printWindow;
};

// CSS for the print toolbar (hidden when printing)
export const getPrintToolbarCSS = () => `
  .print-toolbar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
    padding: 12px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 2px 10px rgba(0,0,0,0.2);
    z-index: 9999;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
  .print-toolbar-title {
    color: white;
    font-size: 14px;
    font-weight: 500;
  }
  .print-toolbar-buttons {
    display: flex;
    gap: 10px;
  }
  .print-btn {
    padding: 8px 20px;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }
  .print-btn-primary {
    background: white;
    color: #1e40af;
  }
  .print-btn-primary:hover {
    background: #f0f9ff;
    transform: scale(1.02);
  }
  .print-btn-secondary {
    background: transparent;
    color: white;
    border: 1px solid rgba(255,255,255,0.5);
  }
  .print-btn-secondary:hover {
    background: rgba(255,255,255,0.1);
  }
  .print-content {
    margin-top: 70px;
    padding: 20px;
  }
  @media print {
    .print-toolbar { display: none !important; }
    .print-content { margin-top: 0; padding: 0; }
  }
`;

// HTML for the print toolbar
export const getPrintToolbarHTML = (title = 'Anteprima Stampa') => `
  <div class="print-toolbar">
    <div class="print-toolbar-title">${title}</div>
    <div class="print-toolbar-buttons">
      <button class="print-btn print-btn-secondary" onclick="window.close()">
        ✕ Chiudi
      </button>
      <button class="print-btn print-btn-primary" onclick="handlePrint()">
        🖨️ Stampa
      </button>
    </div>
  </div>
  <script>
    function handlePrint() {
      try {
        window.print();
      } catch(e) {
        // Fallback for browsers that block window.print()
        document.execCommand('print', false, null);
      }
    }
  </script>
`;

// Wrap content with print toolbar
export const wrapWithPrintToolbar = (content, title = 'Anteprima Stampa') => `
  ${getPrintToolbarHTML(title)}
  <div class="print-content">
    ${content}
  </div>
`;

// Complete print page wrapper
export const createPrintPage = (title, customStyles, bodyContent) => `
  <!DOCTYPE html>
  <html>
  <head>
    <title>${title}</title>
    <style>
      ${getPrintToolbarCSS()}
      ${customStyles}
    </style>
  </head>
  <body>
    ${getPrintToolbarHTML(title)}
    <div class="print-content">
      ${bodyContent}
    </div>
  </body>
  </html>
`;

export default {
  escapeHtml,
  openPrintWindow,
  getPrintToolbarCSS,
  getPrintToolbarHTML,
  wrapWithPrintToolbar,
  createPrintPage
};
